import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BarChart3, Plus } from "lucide-react";
import { getCompanyAnalytics, getScoreDistribution } from "@/lib/api";

const fadeUp = { hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0, transition: { duration: 0.45 } } };

// ─── data normalizers ──────────────────────────────────────────────────────────
// The backend returns:
//   analytics:    { total_applications, by_status, avg_applicant_score, time_to_first_interview_avg_hours }
//   distribution: { bins: { "0-60": n, ... }, total }
// Map those to the shapes this component renders. Returns null / [] when there is
// no real data yet so the page can show an honest empty state (never fake numbers).

const FUNNEL_STAGES = [
  { key: "applied", label: "Applications" },
  { key: "screening", label: "Shortlisted" },
  { key: "interview", label: "Interviewing" },
  { key: "offer", label: "Offered" },
  { key: "hired", label: "Hired" },
];

function normalizeAnalytics(a) {
  if (!a || !a.total_applications) return null;  // empty → caller shows empty state
  const byStatus = a.by_status || {};
  const funnel = FUNNEL_STAGES.map((s) => ({
    stage: s.label,
    count: byStatus[s.key] ?? 0,
  }));
  funnel[0].count = a.total_applications;
  const hours = a.time_to_first_interview_avg_hours;
  return {
    funnel,
    total_applications: a.total_applications,
    avg_score: Math.round(a.avg_applicant_score ?? a.avg_score ?? 0),
    time_to_first_interview_days: hours != null ? +(hours / 24).toFixed(1) : 0,
  };
}

function normalizeDistribution(d) {
  if (!d) return [];
  if (Array.isArray(d)) return d;
  if (Array.isArray(d.distribution)) return d.distribution;
  if (d.bins && typeof d.bins === "object") {
    if (!d.total) return [];
    return Object.entries(d.bins).map(([bin, count]) => ({
      bin: bin.replace("-", "–"),
      count,
    }));
  }
  return [];
}

// Simple CountUp hook
function useCountUp(target, duration = 1200) {
  const [val, setVal] = useState(0);
  const raf = useRef(null);
  const start = useRef(null);

  useEffect(() => {
    if (!target && target !== 0) return;
    start.current = null;
    const step = (ts) => {
      if (!start.current) start.current = ts;
      const progress = Math.min((ts - start.current) / duration, 1);
      setVal(Math.round(progress * target));
      if (progress < 1) raf.current = requestAnimationFrame(step);
    };
    raf.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf.current);
  }, [target, duration]);

  return val;
}

export default function Analytics() {
  const [analytics, setAnalytics] = useState(null);
  const [dist, setDist] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: a }, { data: d }] = await Promise.allSettled([
          getCompanyAnalytics(),
          getScoreDistribution(),
        ]).then((r) => r.map((x) => (x.status === "fulfilled" ? x.value : { data: null })));

        setAnalytics(normalizeAnalytics(a));
        setDist(normalizeDistribution(d));
      } catch {
        setAnalytics(null);
        setDist([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="container-x py-16 text-stone-500 flex items-center justify-center">
        Loading analytics…
      </div>
    );
  }

  // Honest empty state — no applications yet, so no funnel/scores to show
  if (!analytics) {
    return (
      <div className="container-x py-10 md:py-14">
        <motion.div variants={fadeUp} initial="hidden" animate="show">
          <span className="overline">Company</span>
          <h1 className="font-display text-4xl md:text-5xl mt-2">Hiring analytics</h1>
          <p className="text-stone-500 mt-2">Real-time funnel and score insights for your open roles.</p>
        </motion.div>
        <div className="mt-10 rounded-3xl border-2 border-dashed border-stone-300 bg-[#F5F3ED] p-16 text-center">
          <div className="inline-flex p-4 rounded-2xl bg-white text-[#C2410C] mb-4">
            <BarChart3 size={28} />
          </div>
          <h2 className="font-display text-2xl">No application data yet</h2>
          <p className="mt-2 text-stone-600 max-w-md mx-auto">
            Your funnel, conversion rate, and score distribution will appear here as
            soon as candidates start applying to your jobs.
          </p>
          <Link to="/company/jobs/new" className="btn-primary mt-6 inline-flex">
            <Plus size={16} /> Post a job
          </Link>
        </div>
      </div>
    );
  }

  const funnel = analytics.funnel;
  const funnelMax = funnel[0]?.count || 1;

  return (
    <div className="container-x py-10 md:py-14 space-y-10">
      {/* Header */}
      <motion.div variants={fadeUp} initial="hidden" animate="show">
        <span className="overline">Company</span>
        <h1 className="font-display text-4xl md:text-5xl mt-2">Hiring analytics</h1>
        <p className="text-stone-500 mt-2">Real-time funnel and score insights for your open roles.</p>
      </motion.div>

      {/* Key metrics */}
      <motion.div
        variants={fadeUp} initial="hidden" animate="show" transition={{ delay: 0.1 }}
        className="grid grid-cols-1 sm:grid-cols-3 gap-4"
      >
        <MetricCard label="Total applications" value={analytics?.total_applications ?? 0} suffix="" />
        <MetricCard label="Average score"       value={analytics?.avg_score ?? 0} suffix=" pts" />
        <MetricCard label="Avg. days to interview" value={analytics?.time_to_first_interview_days ?? 0} decimals={1} suffix=" days" />
      </motion.div>

      {/* Funnel */}
      <motion.div
        variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
        className="rounded-3xl border border-stone-200 bg-white p-8 md:p-10"
      >
        <span className="overline">Hiring funnel</span>
        <h2 className="font-display text-2xl mt-2 mb-8">Candidate pipeline</h2>
        <div className="space-y-3">
          {funnel.map((stage, i) => {
            const pct = (stage.count / funnelMax) * 100;
            const conv = i === 0 ? 100 : Math.round((stage.count / funnel[0].count) * 100);
            const COLORS = ["#1C1917", "#C2410C", "#D97706", "#15803D", "#60A5FA"];
            return (
              <div key={stage.stage}>
                <div className="flex items-center justify-between text-sm mb-1.5">
                  <span className="font-medium text-stone-700">{stage.stage}</span>
                  <span className="font-mono text-stone-500">
                    <CountUpSpan target={stage.count} /> &nbsp;·&nbsp; {conv}% conversion
                  </span>
                </div>
                <div className="h-9 rounded-xl bg-stone-100 overflow-hidden">
                  <motion.div
                    className="h-full rounded-xl flex items-center px-4"
                    style={{ background: COLORS[i] }}
                    initial={{ width: 0 }}
                    whileInView={{ width: `${pct}%` }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7, delay: i * 0.1, ease: "easeOut" }}
                  >
                    <span className="text-white text-xs font-mono font-bold whitespace-nowrap">
                      {stage.count}
                    </span>
                  </motion.div>
                </div>
              </div>
            );
          })}
        </div>
      </motion.div>

      {/* Score Distribution */}
      <motion.div
        variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
        className="rounded-3xl border border-stone-200 bg-white p-8 md:p-10"
      >
        <span className="overline">Score distribution</span>
        <h2 className="font-display text-2xl mt-2 mb-8">Applicant score breakdown</h2>

        {(() => {
          const distMax = Math.max(...dist.map((d) => d.count), 1);
          const BIN_COLORS = {
            "0–60":   "#78716C",
            "61–70":  "#94A3B8",
            "71–80":  "#D97706",
            "81–90":  "#15803D",
            "91–100": "#60A5FA",
          };
          return (
            <div className="space-y-3">
              {dist.map((bin, i) => {
                const pct = (bin.count / distMax) * 100;
                const color = BIN_COLORS[bin.bin] ?? "#94A3B8";
                return (
                  <div key={bin.bin}>
                    <div className="flex items-center justify-between text-sm mb-1.5">
                      <span className="font-mono text-stone-600 w-16">{bin.bin}</span>
                      <span className="font-mono text-stone-500">
                        <CountUpSpan target={bin.count} /> candidates
                      </span>
                    </div>
                    <div className="h-8 rounded-xl bg-stone-100 overflow-hidden">
                      <motion.div
                        className="h-full rounded-xl"
                        style={{ background: color }}
                        initial={{ width: 0 }}
                        whileInView={{ width: `${pct}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: i * 0.08, ease: "easeOut" }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}
      </motion.div>
    </div>
  );
}

function MetricCard({ label, value, suffix = "", decimals = 0 }) {
  return (
    <motion.div
      variants={fadeUp}
      className="rounded-2xl border border-stone-200 bg-white p-6"
    >
      <p className="overline mb-2">{label}</p>
      <p className="font-display text-4xl">
        <CountUpSpan target={value} decimals={decimals} />{suffix}
      </p>
    </motion.div>
  );
}

function CountUpSpan({ target, decimals = 0 }) {
  const val = useCountUp(target);
  if (decimals > 0) return <>{(val / Math.pow(10, decimals)).toFixed(decimals)}</>;
  return <>{val}</>;
}
